import { env } from '@/shared/config/env';
import type { ApiResponse, ApiResponseList } from './types';

/**
 * Mock-data helpers shared by every entity's MSW handler.
 * They build absolute URLs that match the axios `baseURL` and wrap payloads
 * in the server's `{ success, message, result, error }` envelope.
 */

/** Absolute URL for an MSW handler, matching what axios actually requests. */
export const mockUrl = (path: string): string => `${env.apiBaseUrl}${path}`;

/** Success envelope for a single resource. */
export const mockOk = <T>(result: T, message = ''): ApiResponse<T> => ({
  success: true,
  message,
  result,
  error: { message: '' },
});

/** Error envelope (matches how the interceptor reads `error.message`). */
export const mockFail = (message: string): ApiResponse<null> => ({
  success: false,
  message,
  result: null,
  error: { message },
});

/** Paginated list envelope. Slices `items` for the requested page. */
export const mockPaginated = <T>(items: T[], page = 1, limit = 10): ApiResponseList<T> => {
  const total = items.length;
  const lastPage = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.min(Math.max(1, page), lastPage);
  const start = (currentPage - 1) * limit;
  const pageItems = items.slice(start, start + limit);

  return {
    success: true,
    result: {
      items: pageItems,
      pagination: {
        current_page: currentPage,
        last_page: lastPage,
        limit,
        next_page: currentPage < lastPage ? currentPage + 1 : null,
        prev_page: currentPage > 1 ? currentPage - 1 : null,
        total,
      },
    },
  };
};

/** Reads `page`/`limit`/`search` query params from an MSW request URL. */
export const readListParams = (requestUrl: string) => {
  const { searchParams } = new URL(requestUrl);
  return {
    page: Number(searchParams.get('page')) || 1,
    limit: Number(searchParams.get('per_page') || searchParams.get('limit')) || 10,
    search: searchParams.get('search')?.trim().toLowerCase() ?? '',
  };
};
